import { useContext, useEffect, useState } from "react";
import {
  FaBookOpen,
  FaGraduationCap,
  FaSun,
  FaMoon,
  FaRegBell,
  FaRegFolder,
  FaGlobeAmericas,
  FaUserClock,
} from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { RxHamburgerMenu } from "react-icons/rx";
import { AiOutlineLogout } from "react-icons/ai";
import { Link, Outlet, useLocation } from "react-router-dom";
import { PiStudent } from "react-icons/pi";

import logo from "../assets/logo.png";
import error_logo from "../assets/error_logo.png";
import darkModeLogo from "../assets/darkMode_logo.png";
import { useAuth } from "../Context/AuthContext";
import useDesktopViewport from "../hooks/useDesktopViewport";
import { DarkModeContext } from "../Context/DarkModeContext";
import { GrCatalog } from "react-icons/gr";
import { MdOutlineQrCode } from "react-icons/md";
import {
  BsArrowsCollapseVertical,
  BsArrowsExpandVertical,
} from "react-icons/bs";

function Layout() {
  const { logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useContext(DarkModeContext);
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(
    localStorage.getItem("activeItem") || "",
  );
  const [collapsePreference, setIsCollapsed] = useState(false);
  const isDesktop = useDesktopViewport();
  const isCollapsed = collapsePreference && isDesktop;

  useEffect(() => {
    const img = new Image();
    img.src = error_logo;
  }, []);

  useEffect(() => {
    // Update the active item based on the current path
    let currentActiveItem;
    if (location.pathname === "/announcements") {
      currentActiveItem = "Announcements";
    } else if (location.pathname === "/students") {
      currentActiveItem = "Students";
    } else if (location.pathname === "/courses") {
      currentActiveItem = "Courses";
    } else if (location.pathname === "/folders") {
      currentActiveItem = "Folders";
    } else if (location.pathname === "/academic-levels") {
      currentActiveItem = "Academic Levels";
    } else if (location.pathname === "/stores") {
      currentActiveItem = "Stores";
    } else if (location.pathname === "/codes") {
      currentActiveItem = "Codes";
    } else if (location.pathname.startsWith("/live-sessions")) {
      currentActiveItem = "Live Sessions";
    } else if (location.pathname.startsWith("/catalogue")) {
      currentActiveItem = "Catalogue";
    }

    if (currentActiveItem) {
      setActiveItem(currentActiveItem);
      localStorage.setItem("activeItem", currentActiveItem); // Save to local storage
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    await logout();
  };

  const getNavItemClass = (item) => {
    const isActive = activeItem === item;

    return `flex cursor-pointer items-center rounded-xl transition-all duration-200 ${
      isActive
        ? isDarkMode
          ? "bg-[var(--study-raised)] text-[var(--study-text)] ring-1 ring-inset ring-[var(--study-border)] hover:bg-red-400/20"
          : "bg-customBlue text-white hover:bg-customBlue hover:text-white"
        : "hover:bg-slate-500/10"
    }`;
  };

  return (
    <div
      className={`flex min-h-screen ${
        isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"
      }`}
    >
      {/* Hamburger Menu */}
      {!isSidebarOpen && (
        <nav
          onClick={() => setIsSidebarOpen(true)}
          className={`fixed left-4 top-4 z-50 cursor-pointer rounded-xl bg-customRed p-3 text-white shadow-lg transition hover:scale-105 lg:hidden`}
        >
          <div className="items-center">
            <RxHamburgerMenu size={25} />
          </div>
        </nav>
      )}

      {isSidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={toggleSidebar}
          className="fixed inset-0 z-30 bg-slate-950/45 backdrop-blur-[2px] lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 h-dvh border-r shadow-xl transition-all duration-300 ease-in-out
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
    lg:translate-x-0
    ${isCollapsed ? "w-[76px]" : "w-[248px]"} 
    ${isDarkMode ? "border-gray-700 bg-[#171b22] text-white" : "border-slate-200 bg-white text-customBlue"}`}
      >
        <div className="flex flex-col items-center h-full">
          {/* Logo */}
          <div
            className={`flex min-h-[96px] w-full items-center justify-center max-lg:pl-4 max-lg:pr-16 border-b ${
              isDarkMode ? "border-gray-700" : "border-slate-100"
            } ${isCollapsed ? "py-5" : "px-6 py-4"}`}
          >
            {/* Collapsed logo */}
            <img
              src={error_logo}
              alt="Collapsed Logo"
              className={`w-10 h-10 object-contain absolute transition-all duration-300 ${
                isCollapsed ? "opacity-100 scale-100" : "opacity-0 scale-75"
              }`}
            />

            {/* Expanded logo */}
            <img
              src={isDarkMode ? darkModeLogo : logo}
              alt="Logo"
              className={`w-42 h-21 object-contain transition-all duration-300 ${
                isCollapsed ? "opacity-0 scale-75" : "opacity-100 scale-100"
              }`}
            />
          </div>

          {/* Close Sidebar */}
          <button
            type="button"
            aria-label="Close sidebar"
            title="Close sidebar"
            className={`group absolute right-3 top-7 lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-lg transition duration-200 hover:border-customRed hover:bg-customRed hover:text-white active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customRed focus-visible:ring-offset-2 dark:border-[var(--study-border)] dark:bg-[var(--study-raised)] dark:text-[var(--study-text)] dark:hover:bg-customRed motion-reduce:transition-none`}
            onClick={() => setIsSidebarOpen(false)}
          >
            <IoClose className="text-xl transition-transform duration-200 group-hover:rotate-90 motion-reduce:transition-none" />
          </button>

          {/* Navigation Links */}
          <ul
            onClick={(event) => {
              if (window.innerWidth < 1024 && event.target.closest("a")) {
                setIsSidebarOpen(false);
              }
            }}
            className="min-h-0 w-full flex-1 overflow-y-auto space-y-1 px-3 py-3 text-base"
          >
            <li className={getNavItemClass("Announcements")}>
              <Link
                to="/announcements"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <FaRegBell className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Announcements</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Academic Levels")}>
              <Link
                to="/academic-levels"
                aria-label="Academic Levels"
                title="Academic Levels"
                className={`flex h-full w-full items-center px-3 py-3 ${isCollapsed ? "justify-center w-full" : "justify-start"}`}
              >
                <FaGraduationCap
                  className="shrink-0 text-xl"
                  aria-hidden="true"
                />
                {!isCollapsed && <span className="ml-4">Academic Levels</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Students")}>
              <Link
                to="/students"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <PiStudent className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Students</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Courses")}>
              <Link
                to="/courses"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <FaBookOpen className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Courses</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Folders")}>
              <Link
                to="/folders"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <FaRegFolder className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Folders</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Catalogue")}>
              <Link
                to="/catalogue"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <GrCatalog className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Catalogue</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Stores")}>
              <Link
                to="/stores"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <FaGlobeAmericas className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Stores</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Codes")}>
              <Link
                to="/codes"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <MdOutlineQrCode className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Codes</span>}
              </Link>
            </li>
            <li className={getNavItemClass("Live Sessions")}>
              <Link
                to="/live-sessions"
                className={`flex h-full w-full items-center px-3 py-3 ${
                  isCollapsed ? "justify-center w-full" : "justify-start"
                }`}
              >
                <FaUserClock className={`text-xl`} />
                {!isCollapsed && <span className="ml-4">Live Sessions</span>}
              </Link>
            </li>
          </ul>

          {/* Light/Dark Mode Toggle */}

          {/* Controls (Collapse + Dark Mode) */}
          <div
            className={`flex w-full gap-2 px-3 ${
              isCollapsed ? "flex-col items-center" : "flex-row justify-between"
            }`}
          >
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              aria-label={
                isDarkMode ? "Switch to Light theme" : "Switch to Navy theme"
              }
              title={
                isDarkMode ? "Switch to Light theme" : "Switch to Navy theme"
              }
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 transition-colors duration-300 ${
                isDarkMode
                  ? "border-2 border-gray-500 text-gray-200 hover:bg-gray-700 hover:text-white"
                  : "border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-400  hover:text-white"
              }
                      `}
            >
              {isDarkMode ? <FaMoon /> : <FaSun />}
              {!isCollapsed && (
                <span className="text-xs font-semibold">
                  {isDarkMode ? "Navy" : "Light"}
                </span>
              )}
            </button>

            {/* Collapse / Expand */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 transition-colors duration-300 ${
                isDarkMode
                  ? "border border-[var(--study-border)] text-[var(--study-muted)] hover:bg-white/5 hover:text-white"
                  : "border-2 border-customBlue text-customBlue hover:bg-customBlue hover:text-white"
              }`}
            >
              {isCollapsed ? (
                <>
                  <BsArrowsExpandVertical className="text-xl" />
                </>
              ) : (
                <>
                  <BsArrowsCollapseVertical className="text-xl" />
                </>
              )}
            </button>
          </div>

          {/* Logout Button */}
          <div className="w-full p-3">
            <button
              type="button"
              aria-label="Logout"
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-customRed px-3 py-3 text-base font-semibold text-red-600 transition-all duration-200 hover:bg-red-50 hover:shadow-[0_0_20px_rgba(255,31,57,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 motion-reduce:transition-none"
              onClick={handleLogout}
            >
              <AiOutlineLogout className="text-xl" />

              {!isCollapsed && "Logout"}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`min-w-0 flex-1 transition-all duration-300 ease-in-out
          ${isCollapsed ? "lg:ml-[76px]" : "lg:ml-[248px]"} 
          ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}
      >
        {/* Render child routes/components here */}
        <Outlet />
      </main>
    </div>
  );
}

export default Layout;
